import countries from "world-countries/dist/countries.json";

/**
 * Los catalogos cerrados del formulario.
 *
 * Son datos, no codigo: se ajustan sin tocar ningun componente. Salen de
 * `src/components/fields/*` del builder viejo, para que una HV editada en el
 * editor nuevo guarde exactamente los mismos valores que una editada en el
 * viejo -- mientras conviven, dos vocabularios distintos en la misma columna
 * harian que el tablero "por nivel de formacion" mostrara la mitad de las filas
 * en categorias propias.
 */

export const NIVELES_FORMACION = [
  "Bachiller académico",
  "Técnico",
  "Tecnólogo",
  "Curso",
  "Diplomado",
  "Certificación",
  "Pregrado",
  "Especialización",
  "Maestría",
  "Doctorado",
  "Posdoctorado",
] as const;

export const MODALIDADES = ["Presencial", "Virtual", "Híbrido"] as const;

export const IDIOMAS = [
  "Español",
  "Inglés",
  "Francés",
  "Alemán",
  "Italiano",
  "Portugués",
  "Ruso",
  "Chino",
  "Japonés",
  "Coreano",
] as const;

/**
 * Categorias de discapacidad del Registro de Localizacion y Caracterizacion de
 * Personas con Discapacidad (Minsalud, Resolucion 1239 de 2022).
 *
 * Se usa la clasificacion oficial y no texto libre porque es la que manejan las
 * empresas y las cajas de compensacion en Colombia: "Visual" y "visión baja"
 * escritas a mano no se pueden filtrar juntas en un proceso de seleccion.
 */
export const TIPOS_DISCAPACIDAD = [
  "Física",
  "Auditiva",
  "Visual",
  "Sordoceguera",
  "Intelectual",
  "Psicosocial",
  "Múltiple",
] as const;

export const NIVELES_IDIOMA = ["Básico", "Intermedio", "Avanzado", "Nativo"] as const;

export interface Pais {
  /** ISO 3166-1 alfa-2. Es lo que se guarda en `phone.code`. */
  codigo: string;
  /** Nombre en español. Es lo que se guarda en `country_name`. */
  nombre: string;
  bandera: string;
  prefijo: string;
}

/**
 * Paises con nombre en español, bandera y prefijo telefonico.
 *
 * Se derivan de `world-countries`, que ya era dependencia. El campo de telefono
 * viejo lleva las mismas ~250 entradas escritas a mano dentro del componente
 * (539 lineas): una lista que hay que mantener y que ya diverge de esta.
 */
export const PAISES: Pais[] = (countries as any[])
  .map((c) => {
    const raiz = c.idd?.root ?? "";
    const sufijos: string[] = c.idd?.suffixes ?? [];
    // Paises con muchos sufijos (EEUU, Rusia) usan solo la raiz: listar los
    // cientos de codigos de area no ayuda a nadie a escribir su telefono.
    const prefijo = raiz && sufijos.length === 1 ? `${raiz}${sufijos[0]}` : raiz;
    return {
      codigo: c.cca2 as string,
      nombre: (c.translations?.spa?.common ?? c.name?.common ?? c.cca2) as string,
      bandera: (c.flag ?? "") as string,
      prefijo,
    };
  })
  .filter((p) => p.prefijo)
  .sort((a, b) => a.nombre.localeCompare(b.nombre, "es"));

const PORCODIGO = new Map(PAISES.map((p) => [p.codigo, p]));

export const paisPorCodigo = (codigo: string): Pais | undefined => PORCODIGO.get(codigo);

/** Colombia por defecto: es de donde viene casi todo el trafico del producto. */
export const PAIS_POR_DEFECTO = "CO";

// --- telefono ----------------------------------------------------------------

/**
 * El telefono se guarda como `{ code, value }` con el `value` COMPLETO, prefijo
 * incluido ("+57 3001234567"). Asi esta en la base y asi lo imprime el PDF.
 *
 * Para editarlo hay que separarlo y volver a componerlo, y eso son dos
 * funciones puras: dentro del componente serian imposibles de probar y es
 * justo donde se pierde un numero por un espacio de mas.
 */
export const separarTelefono = (
  guardado: string,
  codigo: string,
): string => {
  const prefijo = paisPorCodigo(codigo)?.prefijo ?? "";
  if (!prefijo) return guardado.trim();
  return guardado.startsWith(prefijo) ? guardado.slice(prefijo.length).trim() : guardado.trim();
};

export const componerTelefono = (
  codigo: string,
  nacional: string,
): { code: string; value: string } | null => {
  const limpio = nacional.trim();
  // Sin numero no hay telefono: se guarda null en vez de un prefijo suelto,
  // que en el PDF saldria como "Teléfono: +57".
  if (!limpio) return null;
  const prefijo = paisPorCodigo(codigo)?.prefijo ?? "";
  return { code: codigo, value: prefijo ? `${prefijo} ${limpio}` : limpio };
};
